/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../base.component';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ModalService, RbacService, UserService, UtilsService } from '../../../services';
import { TableMultiSelectModel } from '../../../models/tableMultiSelect.model';

@Component({
    selector: 'app-rbac-table',
    templateUrl: './rbac-table.component.html',
    styleUrls: ['./rbac-table.component.scss']
})

export class RbacTableComponent extends BaseComponent implements OnInit {
    /**
     * Show import csv
     */
    @Input() set csvSubmissionFields(fields: string[]) {
        this._csvSubmissionFields = fields;
    }

    /**
     * Incoming data from parent component.
     */
    @Input() set data(data: { users: any, roles: any, groups: any }) {
        this._data = data;
        this.populateUserTableRows(data);
        this.populateRolesTableRows(data);
        this.populateGroupsTableRows(data);
        this.populateCsvData();
    }

    /**
     * Button permissions from parent component.
     */
    @Input() set buttonPermissions(permissions: any) {
        this.registerButtons(permissions);
    }

    /**
     * Trigger output for import users.
     */
    @Output() public importUsers: EventEmitter<any> = new EventEmitter();

    /**
     * Trigger output for add edit users.
     */
    @Output() public addEditUser: EventEmitter<any> = new EventEmitter();

    /**
     * Trigger output for add edit roles.
     */
    @Output() public addEditRole: EventEmitter<any> = new EventEmitter();

    /**
     * Trigger output for add edit groups.
     */
    @Output() public addEditGroup: EventEmitter<any> = new EventEmitter();

    /**
     * Trigger output for add edit users.
     */
    @Output() public deleteUser: EventEmitter<any> = new EventEmitter();

    /**
     * Trigger output for add edit users.
     */
    @Output() public deleteRole: EventEmitter<any> = new EventEmitter();

    /**
     * Trigger output for add edit users.
     */
    @Output() public deleteGroup: EventEmitter<any> = new EventEmitter();

    /**
     * Store the selectAll data.
     */
    public multiSelectActions: TableMultiSelectModel[] = [];

    /**
     * Store users, groups and roles table rows.
     */
    public userTableRows: any[] = [];
    public rolesTableRows: any[] = [];
    public groupsTableRows: any[] = [];

    /**
     * Prepared data for CSV importer.
     */
    public dataToMatch: any;

    /**
     * Store the active tab ID.
     */
    public activeTabId: string = 'users-list';

    /**
     * Store the product name.
     */
    public isEndurance: boolean = false;

    /**
     * To store the logged-in user.
     * @private
     */
    private loggedInUser: string = '';

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Should show hint text.
     */
    public hintText: boolean = false;

    /**
     * Store the current users ID.
     */
    public userId: number;

    /**
     * Store selected row object for table state checks.
     */
    public selectedRows: any = [];

    /**
     * csvSubmissionFields
     */
    private _csvSubmissionFields: string [] = [];

    /**
     * Store api data.
     * @private
     */
    private _data: any;

    constructor(
        private modalService: ModalService,
        private rbacService: RbacService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.setSelectAll();
    }

    get csvSubmissionFields(): string[] {
        return this._csvSubmissionFields;
    }

    get data() {
        return this._data;
    }

    public ngOnInit(): void {
        this.userId = this.userService.getUser('id');
        this.isEndurance = this.environment.module === this.modulesEnum.LOADBALANCER;
        this.loggedInUser = this.userService.getUser();
    }

    /**
     * Set the active tab.
     * @param $event
     */
    public setActiveTab($event: any): void {
        this.activeTabId = $event.id;
    }

    /**
     * Disabled edit user button
     */
    public disabledUserEditButton(row: any): boolean {
        if (this.loggedInUser['id'] === row.id.toString()) {
            return true;
        } else {
            if (row.roles.includes('owner')) {
                // Don't allow the last owner to downgrade their permissions.
                const owners: any[] = this.userTableRows.filter((row: any): any => row.roles?.includes('owner'));
                this.hintText = owners.length < 2;

                return !this.permissionsLookup['rbac-user-edit'] || owners?.length < 2;
            }

            return !this.permissionsLookup['rbac-user-edit'];
        }
    }

    /**
     * Stop you deleting yourself.
     * @param row
     */
    public disabledUserDeleteButton(row: any): boolean {
        return !this.permissionsLookup['rbac-user-delete'] || row.id?.toString() === this.userId;
    }

    /**
     * Is not a feature role.
     * @param row
     */
    public isNotFeatureRole(row: any): boolean {
        return ![1, 2, 3, 4].includes(row.id) && row.name !== 'System Admin';
    }

    /**
     * Import submit.
     * @param event
     */
    public importerSubmit(event: any): void {
        this.importUsers.next(event);
    }

    /**
     * Form function triggered when a table row is selected.
     * @param event
     */
    public onSelect(event: any): void {
        this.selectedRows = event;
    }

    /**
     * Confirm deletion of user, group or role.
     * @param type
     * @param data
     */
    public confirmDelete(type: string, data?: any): void {
        const submitData = data ? data : this.selectedRows;
        const { title, message, callBack } = this.getDeleteObject(type, submitData);

        const confirmationObject: any = {
            title: title,
            message: message,
            callBack: callBack,
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Get confirmation delete object depending on tab selected.
     * @param type
     * @param data
     * @private
     */
    private getDeleteObject(type: string, data: any): any {
        let title: string;
        let message: string;
        let callBack: (...args: any[]) => void;
        switch (type) {
            case 'user':
                title = 'delete_user';
                message = this.isEndurance ? 'user_delete_message_endurance' : 'user_delete_message_portal';
                callBack = () => this.deleteUser.next(data);
                break;
            case 'users':
                title = 'delete_users';
                message = this.isEndurance ? 'user_delete_message_endurance' : 'user_delete_users_message_portal';
                callBack = () => this.deleteUser.next(data);
                break;
            case 'role':
                title = 'user_delete_role';
                message = 'user_delete_role_message';
                callBack = () => this.deleteRole.next(data);
                break;
            case 'group':
                title = 'user_delete_group';
                message = 'user_delete_group_message';
                callBack = () => this.deleteGroup.next(data);
                break;
        }

        return { title, message, callBack };
    }

    /**
     * Populate user table rows.
     * @param data
     * @private
     */
    private populateUserTableRows(data: any): void {
        this.userTableRows = [];

        if (data.users) {
            for (const user of data.users) {
                this.userTableRows.push(
                    {
                        id: user.id,
                        name: user.name,
                        username: user.email,
                        status: this.getStatus(user),
                        roles: data.roles ? this.getMatched(user.roles, data.roles, 'name') : [],
                        groups: data.groups ? this.getMatched(user.groups, data.groups, 'name') : [],
                        isUserLoggedIn: this.loggedInUser['email'] === user['email'],
                        lastLoggedIn: user.lastLoggedIn ? this.utilsService.createDateTimeString(user.lastLoggedIn) : '-',
                        otpEnabled: user.otpEnabled
                    }
                );
            }
        }
    }

    /**
     * Format status
     * @param user
     * @private
     */
    private getStatus(user: any): string {
        if (this.isEndurance) {
            return user.disabled ? 'disabled' : 'enabled';
        } else {
            return user.activated ? 'active' : 'invited';
        }
    }

    /**
     * Populate roles rows.
     * @param data
     * @private
     */
    private populateRolesTableRows(data: any): void {
        this.rolesTableRows = [];
        if (data.roles) {
            for (const role of data.roles) {
                this.rolesTableRows.push(
                    {
                        id: role.id,
                        name: role.name,
                        accounts: role.accounts || [],
                        users: this.getMatchedUsers(role),
                        groups: this.getFriendlyGroupNames(role),
                        userHasRole: this.userHasRole(role),
                        canEditRole: this.permissionsLookup['rbac-role-edit'] && !this.userHasRole(role),
                        canDeleteRole: this.permissionsLookup['rbac-role-delete'] && !this.userHasRole(role),
                    }
                );
            }
        }
    }

    /**
     * Get group names to display in table.
     * @param role
     * @private
     */
    private getFriendlyGroupNames(role: any): string {
        const matchedGroups: any[] = this.getMatchedGroups(role);
        const matchedGroupNames: any[] = [];
        for (const group of matchedGroups) {
            matchedGroupNames.push(group.name);
        }

        return this.utilsService.replaceLastCommaWithAnd(matchedGroupNames.toString(), true);
    }

    /**
     * Get matched users for role.
     * @param role
     * @private
     */
    private getMatchedUsers(role: any): any[] {
        const accounts: string = this.isEndurance ? 'users' : 'accounts';

        return this.data['users'].filter((item: any): any => role[accounts]?.includes(item.id.toString())) || [];
    }

    /**
     * Get matched groups.
     * @param role
     * @private
     */
    private getMatchedGroups(role: any): any[] {
        return this.data['groups'].filter((item: any): any => role.groups?.includes(item.id.toString())) || [];
    }

    /**
     * Determine if the group is assigned to the user.
     * @param group
     * @private
     */
    private groupIsAssignedToUser(group: any): boolean {
        const accounts = this.isEndurance ? group.users : group.accounts;

        return accounts?.includes(this.userId);
    }

    /**
     * Get current user's groups.
     * @private
     */
    private getUsersGroups(): any[] {
        const accounts: string = this.isEndurance ? 'users' : 'accounts';

        return this.data['groups'].filter((item: any) => item[accounts]?.includes(this.userId)) || [];
    }

    /**
     * Determine if the user has the given role assigned to it.
     * @param role
     * @private
     */
    private userHasRole(role: any): boolean {
        const usersGroups: any[] = this.getUsersGroups();
        const associatedGroupHasRole: boolean = this.associatedGroupHasRole(usersGroups, role);
        const accounts = this.isEndurance ? role.users : role.accounts;

        return accounts?.includes(this.userId) || associatedGroupHasRole;
    }

    /**
     * Check if a group the current user belongs to contains the group to be edited.
     * @param usersGroups
     * @param row
     * @private
     */
    private associatedGroupHasRole(usersGroups: any[], row: any): boolean {
        return usersGroups.some(group => group['roles']?.includes(row.id.toString()));
    }

    /**
     * Populate groups rows.
     * @param data
     * @private
     */
    private populateGroupsTableRows(data: any): void {
        this.groupsTableRows = [];
        if (data.groups) {
            for (const group of data.groups) {
                this.groupsTableRows.push(
                    {
                        id: group.id,
                        name: group.name,
                        users: this.getMatched(group.users, data.users),
                        roles: this.getMatched(group.roles, data.roles, 'name'),
                        userHasGroup: this.groupIsAssignedToUser(group),
                        canEditGroup: this.permissionsLookup['rbac-group-edit'] && !this.groupIsAssignedToUser(group),
                        canDeleteGroup: this.permissionsLookup['rbac-group-delete'] && !this.groupIsAssignedToUser(group)
                    }
                );
            }
        }
    }

    /**
     * Get matched item from list via lookup
     * @param lookupIds - array of Ids
     * @param list - list of object to lookup ids from
     * @param property - if supplied the returned list with be based on the property.
     * @private
     */
    private getMatched(lookupIds: string[], list: any[], property?: string): any {
        const matchedRoles: any[] = [];
        if (lookupIds?.length && list?.length) {
            for (const id of lookupIds) {
                const match = list.find((item: any): boolean => item.id?.toString() === id.toString());
                if (match) {
                    matchedRoles.push(match[property] ? match[property] : match);
                }
            }
        }

        return property ? this.utilsService.replaceLastCommaWithAnd(matchedRoles.toString(), true) : matchedRoles;
    }

    /**
     * Populate CSV data match.
     * @private
     */
    private populateCsvData(): void {
        this.dataToMatch = {
            roles: this.rolesTableRows.map(item => {
                return {
                    id: item.id,
                    name: item.name,
                };
            }),
            groups: this.groupsTableRows.map(item => {
                return {
                    id: item.id,
                    name: item.name,
                };
            }),
        };
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(buttonPermissions: any): void {
        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonPermissions);
    }

    /**
     * Set the select all button.
     */
    public setSelectAll(): void {
        this.multiSelectActions = [
            {
                buttonText: 'rbac_users_delete_selected',
                buttonAction: this.confirmDelete.bind(this, 'users'),
                buttonDisabled: false
            },
        ];
    }
}
